import { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from "react";
import { Upload, X, FileText, Loader2, Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "./adminApi";

const ALL_DOC_TYPES = [
  { key: "cv", label: "CV" },
  { key: "id_card_recto", label: "CNI / Passeport — recto" },
  { key: "id_card_verso", label: "CNI — verso" },
  { key: "id_card", label: "Pièce d'identité (unique)" },
  { key: "carte_sejour_recto", label: "Carte de séjour — recto" },
  { key: "carte_sejour_verso", label: "Carte de séjour — verso" },
  { key: "carte_sejour", label: "Carte de séjour (unique)" },
  { key: "attestation_secu", label: "Attestation Sécu" },
  { key: "justificatif_domicile", label: "Justificatif domicile" },
  { key: "certificat_travail", label: "Certificat de travail / bulletins" },
  { key: "visite_medicale", label: "Visite médicale" },
  { key: "rqth", label: "RQTH" },
  { key: "caces_habilitations", label: "CACES / habilitations" },
  { key: "carte_btp", label: "Carte BTP (facultatif)" },
  { key: "test_securite", label: "Tests sécurité (admin uniquement)" },
  { key: "rib", label: "RIB (salarié uniquement)" },
  { key: "other", label: "Autre document" },
];

// Types autorisant l'upload multi-fichiers (typiquement les documents libres)
const MULTI_UPLOAD_TYPES = new Set(["other", "caces_habilitations", "certificat_travail"]);

const MAX_SIZE = 10 * 1024 * 1024;

const AdminUploadDocButton = forwardRef(function AdminUploadDocButton({ userId, role, existingTypes = [], onUploaded }, ref) {
  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState("");
  const [files, setFiles] = useState([]); // [{ file, status, error }]
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // Expose imperative API: parent can call ref.current.openWith("id_card")
  // to open the dialog pre-filled with a specific doc_type (used by
  // ComplianceChecklist tiles with status="missing").
  useImperativeHandle(ref, () => ({
    openWith: (preselectDocType) => {
      setFiles([]);
      setDocType(preselectDocType || "");
      setOpen(true);
    },
  }), []);

  useEffect(() => {
    if (!open) { setDocType(""); setFiles([]); }
  }, [open]);

  const filteredDocTypes = ALL_DOC_TYPES.filter((d) => {
    if (d.key === "rib" && role !== "employee") return false;
    return true;
  });

  const isMulti = MULTI_UPLOAD_TYPES.has(docType);

  const addFiles = useCallback((fileList) => {
    if (!fileList || !fileList.length) return;
    const arr = Array.from(fileList);
    if (!isMulti && arr.length > 1) {
      toast.warning("Ce type de document n'accepte qu'un seul fichier — seul le premier sera pris en compte.");
    }
    const kept = (isMulti ? arr : [arr[0]]).filter((f) => {
      if (f.size > MAX_SIZE) {
        toast.error(`"${f.name}" dépasse la limite de 10 Mo`);
        return false;
      }
      return true;
    }).map((f) => ({ file: f, status: "pending", error: null }));
    setFiles((prev) => (isMulti ? [...prev, ...kept] : kept));
  }, [isMulti]);

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!docType) { toast.error("Sélectionnez d'abord un type de document"); return; }
    addFiles(e.dataTransfer.files);
  };

  const handleSubmit = async () => {
    if (!docType) { toast.error("Sélectionnez un type de document"); return; }
    if (files.length === 0) { toast.error("Sélectionnez au moins un fichier"); return; }
    setUploading(true);
    let ok = 0;
    let ko = 0;
    for (let i = 0; i < files.length; i++) {
      setFiles((prev) => prev.map((f, idx) => idx === i ? { ...f, status: "uploading" } : f));
      try {
        const fd = new FormData();
        fd.append("doc_type", docType);
        fd.append("file", files[i].file);
        await api.post(`/admin/candidates/${userId}/documents/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => prev.map((f, idx) => idx === i ? { ...f, status: "done" } : f));
        ok += 1;
      } catch (err) {
        setFiles((prev) => prev.map((f, idx) => idx === i ? { ...f, status: "error", error: err.response?.data?.detail || "Erreur" } : f));
        ko += 1;
      }
    }
    setUploading(false);
    if (ok > 0) toast.success(`${ok} document${ok > 1 ? "s" : ""} déposé${ok > 1 ? "s" : ""}${ko > 0 ? ` — ${ko} échec(s)` : ""}`);
    else if (ko > 0) toast.error(`${ko} échec(s) sur ${files.length}`);
    if (ok > 0 && ko === 0) {
      // Full success → close & reset
      setTimeout(() => {
        setOpen(false);
        setDocType("");
        setFiles([]);
        if (inputRef.current) inputRef.current.value = "";
      }, 700);
    }
    onUploaded?.();
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="rounded-none border-[#662483] text-[#662483] text-xs h-8"
        data-testid="admin-upload-doc-btn"
      >
        <Upload className="w-3.5 h-3.5 mr-1" /> Déposer un document
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="text-[#3F3F3F]">Déposer un document au nom du candidat</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-xs text-gray-500">
              Utile pour ajouter une pièce que le candidat n&apos;a pas téléversée à l&apos;inscription. Chiffré et tracé (audit log).
            </p>
            <div>
              <Label className="text-xs font-semibold text-gray-700 mb-1 block">Type de document</Label>
              <Select value={docType} onValueChange={(v) => { setDocType(v); setFiles([]); }}>
                <SelectTrigger className="rounded-none" data-testid="admin-upload-doc-type">
                  <SelectValue placeholder="Choisir un type..." />
                </SelectTrigger>
                <SelectContent>
                  {filteredDocTypes.map((d) => {
                    const already = existingTypes.includes(d.key);
                    return (
                      <SelectItem key={d.key} value={d.key}>
                        {d.label}
                        {MULTI_UPLOAD_TYPES.has(d.key) && <span className="text-[#662483] text-[10px] ml-2">(multi-fichiers)</span>}
                        {already && !MULTI_UPLOAD_TYPES.has(d.key) && <span className="text-amber-600 text-[10px] ml-2">(remplacera l&apos;existant)</span>}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Drag & drop zone */}
            <div>
              <Label className="text-xs font-semibold text-gray-700 mb-1 block">
                Fichier{isMulti ? "s" : ""} (PDF, JPG, PNG — max 10 Mo par fichier)
              </Label>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${dragOver ? "border-[#662483] bg-[#F0E9F3]" : "border-gray-300 bg-[#FAFAFA] hover:border-[#662483] hover:bg-[#F0E9F3]/40"}`}
                data-testid="admin-upload-dropzone"
              >
                <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? "text-[#662483]" : "text-gray-400"}`} />
                <p className="text-sm font-semibold text-[#3F3F3F]">
                  {isMulti ? "Glissez-déposez vos fichiers ici" : "Glissez-déposez un fichier ici"}
                </p>
                <p className="text-[11px] text-gray-500 mt-1">ou cliquez pour parcourir</p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".pdf,image/jpeg,image/png,image/webp"
                  multiple={isMulti}
                  onChange={(e) => addFiles(e.target.files)}
                  className="hidden"
                  data-testid="admin-upload-doc-file"
                />
              </div>
              {files.length > 0 && (
                <div className="mt-3 border border-gray-200 divide-y divide-gray-100 max-h-52 overflow-y-auto" data-testid="admin-upload-file-list">
                  {files.map((f, idx) => (
                    <div key={`${f.file.name}-${idx}`} className="flex items-center gap-2 p-2 text-xs" data-testid={`admin-upload-file-item-${idx}`}>
                      <FileText className="w-3.5 h-3.5 text-[#662483] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#3F3F3F] truncate">{f.file.name}</p>
                        <p className="text-[10px] text-gray-500">{(f.file.size / 1024).toFixed(1)} Ko</p>
                        {f.error && <p className="text-[10px] text-red-500">{f.error}</p>}
                      </div>
                      {f.status === "uploading" && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#662483]" />}
                      {f.status === "done" && <Check className="w-3.5 h-3.5 text-green-600" />}
                      {f.status === "error" && <AlertCircle className="w-3.5 h-3.5 text-red-500" />}
                      {f.status === "pending" && !uploading && (
                        <button onClick={() => removeFile(idx)} className="p-1 hover:bg-red-50 text-red-500" title="Retirer" data-testid={`admin-upload-remove-${idx}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSubmit}
                disabled={uploading || !docType || files.length === 0}
                className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none flex-1"
                data-testid="admin-upload-doc-submit"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Téléversement..." : `Déposer ${files.length > 1 ? `${files.length} fichiers` : "le fichier"}`}
              </Button>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={uploading} className="rounded-none">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default AdminUploadDocButton;
